import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Languages, Menu, X } from "lucide-react";

import { getLogoWhite } from "@/utils/assets";
import { content } from "@/i18n/content";
import { useLang } from "@/i18n/language";
import { portalContent } from "@/i18n/portal";
import { supabase } from "@/integrations/supabase/client";

const sections = ["about", "experience", "pathways", "included", "enroll"] as const;

export function SiteHeader() {
  const logo = getLogoWhite();
  const { lang, toggle } = useLang();
  const t = content[lang];
  const p = portalContent[lang];
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSignedIn(Boolean(data.session)));
    const { data } = supabase.auth.onAuthStateChange((_e, session) =>
      setSignedIn(Boolean(session)),
    );
    return () => data.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-background/90 backdrop-blur border-b border-border" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-18 max-w-6xl items-center justify-between px-5 py-4 md:px-8">
        <a href="#top" className="flex items-center gap-3">
          <img src={logo.url} alt="Share The Med" className="h-9 w-auto" />
          <span className="hidden text-sm font-semibold tracking-[0.22em] uppercase sm:block">
            Share <span className="text-primary">the</span> Med
          </span>
        </a>

        <nav className="hidden items-center gap-7 lg:flex">
          {sections.map((key) => (
            <a
              key={key}
              href={`#${key}`}
              className="text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              {t.nav[key]}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            aria-label="Change language"
            className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold tracking-widest uppercase text-muted-foreground transition-colors hover:border-primary hover:text-primary"
          >
            <Languages className="size-3.5" />
            {lang === "es" ? "EN" : "ES"}
          </button>
          {signedIn ? (
            <Link
              to="/portal"
              className="hidden rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 sm:inline-block"
            >
              {p.portal.title}
            </Link>
          ) : (
            <Link
              to="/auth"
              className="hidden rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 sm:inline-block"
            >
              {t.cta.apply}
            </Link>
          )}
          <button
            className="lg:hidden"
            aria-label="Menu"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border bg-background/95 backdrop-blur lg:hidden">
          <nav className="mx-auto flex max-w-6xl flex-col gap-1 px-5 py-4">
            {sections.map((key) => (
              <a
                key={key}
                href={`#${key}`}
                onClick={() => setOpen(false)}
                className="rounded-md px-2 py-2.5 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                {t.nav[key]}
              </a>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
